import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { HeartHandshake } from 'lucide-react';
import { toast } from 'sonner';
import { care, errMsg } from './careApi';
import { Btn, Input } from './careUi';

const Shell = ({ title, sub, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-white">
    <div className="hidden lg:flex flex-col justify-between bg-[#181B30] p-12 text-white">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center"><HeartHandshake className="w-5 h-5" /></div>
        <span className="font-bold text-lg">StayMii Care</span>
      </div>
      <div>
        <h2 className="text-4xl font-bold leading-tight">Resident records, admission documents and billing — in one place.</h2>
        <p className="text-slate-400 mt-4 max-w-md">Internal system for authorised nursing home staff only.</p>
      </div>
      <p className="text-xs text-slate-500">Powered by StayMii™</p>
    </div>
    <div className="flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

export function CareLogin() {
  const nav = useNavigate();
  const [f, setF] = useState({ email: '', password: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const { data } = await care.post('/auth/login', f);
      localStorage.setItem('care_token', data.token);
      localStorage.setItem('care_user', JSON.stringify(data.user));
      nav('/care/dashboard');
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Sign in" sub="Use your StayMii Care company account.">
      <form onSubmit={submit} className="space-y-4" data-testid="care-login-form">
        <Input label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} testid="care-login-email" />
        <Input label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="care-login-password" />
        {err && <p className="text-sm text-rose-600" data-testid="care-login-error">{err}</p>}
        <Btn type="submit" loading={busy} className="w-full" data-testid="care-login-submit">Sign in</Btn>
        <Link to="/care/forgot-password" className="block text-center text-sm text-teal-700 hover:underline" data-testid="care-forgot-link">Forgot password?</Link>
        <Link to="/care/owner-setup" className="block text-center text-xs text-slate-400 hover:text-slate-600" data-testid="care-owner-setup-link">StayMii owner? Set up Care admin</Link>
      </form>
    </Shell>
  );
}

export function CareForgot() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await care.post('/auth/forgot-password', { email, origin_url: window.location.origin }); setSent(true); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Reset password" sub="We'll email you a reset link valid for 1 hour.">
      {sent ? <p className="text-sm text-emerald-700" data-testid="care-forgot-sent">If the email is registered, a reset link has been sent.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Input label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} testid="care-forgot-email" />
          <Btn type="submit" loading={busy} className="w-full" data-testid="care-forgot-submit">Send reset link</Btn>
        </form>
      )}
      <Link to="/care/login" className="block mt-4 text-center text-sm text-teal-700 hover:underline">Back to sign in</Link>
    </Shell>
  );
}

export function CareReset() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState({ a: '', b: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw.a !== pw.b) return toast.error('Passwords do not match');
    setBusy(true);
    try { await care.post('/auth/reset-password', { token: sp.get('token'), password: pw.a }); toast.success('Password updated. Please sign in.'); nav('/care/login'); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Choose a new password" sub="At least 8 characters.">
      <form onSubmit={submit} className="space-y-4">
        <Input label="New password" type="password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} testid="care-reset-password" />
        <Input label="Confirm password" type="password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} testid="care-reset-confirm" />
        <Btn type="submit" loading={busy} className="w-full" data-testid="care-reset-submit">Update password</Btn>
      </form>
    </Shell>
  );
}
